import { format, parseISO } from "date-fns";
import styles from "../../styles/components/FileDetail.module.scss";

import word_icon from "../../assets/icons/file_types/word_icon.svg";
import pdf_icon from "../../assets/icons/file_types/pdf_icon.svg";
import excel_icon from "../../assets/icons/file_types/excel_icon.svg";
import mail_icon from "../../assets/icons/file_types/mail_icon.svg";
import text_icon from "../../assets/icons/file_types/text_icon.svg";
import unknown_icon from "../../assets/icons/file_types/unknown_icon.svg";
import powerpoint_icon from "../../assets/icons/file_types/powerpoint_icon.svg";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { RootState } from "../../store";
import { setFavoriteDocuments, setNotification } from "../../redux/actions";
import { downloadFile } from "../../functions/actions/documents";
import { getItemById } from "../../functions/convert";
import axios from "src/axios/axios";
import useAuth from "src/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { getWindevLang } from "src/lib/lang";

type Props = {
    viewFile: () => void;
    document: DocumentObject
}

const FileDetail : React.FC<Props> = ({viewFile, document}) => {
    const {t,i18n} = useTranslation()
    const dispatch = useAppDispatch();
    const {auth} = useAuth()
    const favoriteDocuments:DocumentObject[] = useAppSelector((state:RootState) => state.favoriteDocuments);
    const metatypeList:MetatypeObject[] = useAppSelector((state:RootState) => state.metatypes);

    const handleFavoriteFile = async (item:DocumentObject) => {
        dispatch(setNotification({type:'pending', title:t('Even geduld'), message:t('Het document wordt bij uw favorieten toegevoegd')}))
        
        let tmp_favoriteDocuments = [...favoriteDocuments];
        const index = tmp_favoriteDocuments.findIndex(i => i.docid === item.docid)
        if(index === -1){
            let {data:res} = await axios.post(`favorites/add`, {token:auth.data?.token, docid:item.docid})
            if(res.errorcode === 0){
                tmp_favoriteDocuments.push(item);
                dispatch(setFavoriteDocuments(tmp_favoriteDocuments))
                dispatch(setNotification({type:'success', title:t('Succes'), message:t('Uw document werd toegevoegd aan favorieten')}))
            }
            else {
                console.log(res)
                dispatch(setNotification({type:'error', title:t('Error'), message:t('Er liep iets mis bij het toevoegen aan favorieten')}))
            }
        }
        else {
            let {data:res} = await axios.delete(`favorites/delete?docid=${item.docid}`)
            if(res.errorcode === 0){
                tmp_favoriteDocuments.splice(index, 1)
                dispatch(setFavoriteDocuments(tmp_favoriteDocuments))
                dispatch(setNotification({type:'success', title:t('Succes'), message:t('Uw document werd verwijderd uit favorieten')}))
            }
            else {
                console.log(res)
                dispatch(setNotification({type:'error', title:t('Error'), message:t('Er liep iets mis bij het verwijderen aan favorieten')}))
            }
        }
    }

    const handleDownloadFile = async (item:DocumentObject) => {
        dispatch(setNotification({type:'pending', title:t('Even geduld'), message:t('Uw document wordt opgehaald')}))
        let {data:res} = await axios.get(`documents/get?docid=${item.docid}`)
        if(res.errorcode === 0){
            if(res.object.file){
                downloadFile(res.object.file, item.filename)
                dispatch(setNotification({type:'success', title:t('Succes'), message:t('Uw document werd gedownload')}))
            }
            else {
                console.log(res)
                dispatch(setNotification({type:'error', title:t('Error'), message:t('Er liep iets mis tijdens het downloaden')}))
            }
        }
        else {
            console.log(res)
            dispatch(setNotification({type:'error', title:t('Error'), message:t('Er liep iets mis tijdens het downloaden')}))
        }
    }

    const getMetatypeOMS = (metatype:string) => {
        let value = '';
        const item = getItemById(metatypeList, "metatype", metatype)
        if(item?.metatype_oms){
            item.metatype_oms.forEach(oms => {
                if(oms.nationid === getWindevLang(i18n.language)){
                    value = oms.oms
                }
            });
        }
        return value
    }

    const extensionToIcon = (extension:string) => {
        switch (extension) {
            case 'doc': case 'docx' :
                return word_icon
            case 'xlsx':
                return excel_icon
            case 'pdf':
                return pdf_icon
            case 'txt':
                return text_icon
            case 'ppt':
                return powerpoint_icon
            case 'eml':
                return mail_icon;
            default:
                return unknown_icon
        }
    }

    return (
        <>
        <div className={styles.filedetail}>
            <div className={styles.filedetail_content}>
                <div className={styles.header}>
                    <svg className={styles.icon} width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 19.25C15.3325 19.25 19.25 15.3325 19.25 10.5C19.25 5.66751 15.3325 1.75 10.5 1.75C5.66751 1.75 1.75 5.66751 1.75 10.5C1.75 15.3325 5.66751 19.25 10.5 19.25Z" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M10.5 14V10.5" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M10.5 7H10.5088" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    <p className={styles.title}>{t('Bestandsinformatie')}</p>
                </div>
                <div className={styles.top}>
                    <div className={styles.image}>
                        <img className={styles.filetype} src={extensionToIcon(document.filename.split('.')[document.filename.split('.').length - 1])} alt="file type"/>
                        <svg width="59" height="74" viewBox="0 0 59 74" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M33.25 1H8.16667C6.26595 1 4.44308 1.75506 3.09907 3.09907C1.75506 4.44308 1 6.26595 1 8.16667V65.5C1 67.4007 1.75506 69.2236 3.09907 70.5676C4.44308 71.9116 6.26595 72.6667 8.16667 72.6667H51.1667C53.0674 72.6667 54.8903 71.9116 56.2343 70.5676C57.5783 69.2236 58.3333 67.4007 58.3333 65.5V26.0833L33.25 1Z" stroke="#E0E0E0" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                    </div>
                    <p className={styles.filename}>{document.filename}</p>
                </div>

                <div className={styles.body}>
                    <div className={styles.option}>
                        <p className={styles.label}>Document ID</p>
                        <p className={styles.value}>{document.docid}</p>
                    </div>

                    <div className={styles.option}>
                        <p className={styles.label}>{t('Aangemaakt op')}</p>
                        <p className={styles.value}>{format(parseISO(document.docdate), "d MMMM yyyy")}</p>
                    </div>

                    <div className={styles.option}>
                        <p className={styles.label}>{t('Documenttype')}</p>
                        <p className={styles.value}>{document.doctype}</p>
                    </div>

                    {document.metadata.map((value, index) => {
                        return (
                            <div className={styles.option} key={index}>
                                <p className={styles.label}>{getMetatypeOMS(value.metatype)}</p>
                                <p className={styles.value}>{value.metavalue}</p>
                            </div>
                        )
                    })}
                    
                </div>
            </div>

            <div className={styles.bottom}>
                <button onClick={()=>{viewFile()}} className={styles.option}>
                    <svg className={styles.icon} viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                    <p className={styles.label}>{t('Bekijken')}</p>
                </button>

                <button onClick={()=>{handleDownloadFile(document)}} className={styles.option}>
                    <svg className={styles.icon} viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                    <p className={styles.label}>{t('Download')}</p>
                </button>

                <button onClick={()=>{handleFavoriteFile(document)}} className={styles.option}>
                    <svg className={`${favoriteDocuments.findIndex(i => i.docid.toString() === document.docid.toString()) > -1 && styles.active_favorite} ${styles.icon}`} viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
                    <p className={styles.label}>{t('Favoriet')}</p>
                </button>
                
            </div>
        </div>
        
        </>
    )
}

export default FileDetail;